import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { ArrowLeft, ArrowUpRight, Check, Menu, Minus, TrendingDown, TrendingUp, Utensils } from 'lucide-react';
import { IoChevronDown } from 'react-icons/io5';
import { formatQuantityParts } from '../utils/unitDisplay';
import { highlightMatch } from '../styles/standardStyles';
// The row and its expanded panel are styled entirely by the Stock Report's stylesheet.
// Importing it here means any screen that renders SalesItemRow gets the identical look
// without having to know which CSS file it lives in.
import './stockrepo.css';

/**
 * ONE Item-Wise Sales Report row, and the cost breakdown behind it.
 *
 * WHY THIS IS ITS OWN MODULE
 * --------------------------
 * Two screens publish the Item-Wise Sales Report: the Stock Report's "Detailed Costing"
 * table and the Dashboard's "View Table". They render the same rows from the same
 * endpoint, so they have to look and read the same. When the Dashboard grew its own
 * table markup, they immediately did not: one screen showed an item as a card with its
 * photo, its ingredients and the Price - Cost = Profit line, the other showed it as a
 * spreadsheet row. Same report, same numbers, two presentations.
 *
 * The fix is that there is only one row component, and it lives here. Both screens
 * import it. A change to how an item reads happens once and lands on both.
 *
 * Everything below was moved verbatim out of stockrepo.js, which now imports it back.
 */

const API_BASE_URL = process.env.REACT_APP_API_URL;

const isPlaceholderImage = (url) => {
    if (!url) return true;
    const str = String(url).toLowerCase();
    return str.includes('default.png') || str.includes('placeholder') || str === 'null' || str === 'undefined' || str === '';
};

// ───────────────────────────────────────────────────────────────────────────────
// Avg sold vs menu price — where the gap comes from.
//
// The card prints the realised price beside today's menu price. When they differ the
// reader needs to know whether it was a discount, a bill above the menu, or sales
// booked from a POS name that was mapped onto this dish at that name's own price.
// The backend splits revenue exactly (utils/menuPriceVariance.js); this only shows it.
// ───────────────────────────────────────────────────────────────────────────────

const pvMoney = (v) => `₹${Math.abs(Number(v) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pvSigned = (v) => `${Number(v) < 0 ? '−' : '+'}${pvMoney(v)}`;
const pvPlates = (n) => `${Number(n)} plate${Number(n) === 1 ? '' : 's'}`;

export const PriceVarianceNote = ({ pv }) => {
    if (!pv || !(pv.plates > 0) || !pv.reconciles) return null;
    if (Math.abs(pv.revenue - pv.menuValue) < 0.005) return null;
    const avg = pv.revenue / pv.plates;
    const rows = [];
    if (pv.mapped && pv.mapped.plates > 0 && Math.abs(pv.mapped.gap) >= 0.005) {
        const names = (pv.mapped.names || []).map((n) => `"${n}"`).join(', ') || 'a POS name';
        rows.push({ key: 'mapped', label: `Booked from POS ${names} (mapped from Not in Menu, ${pvPlates(pv.mapped.plates)} at the POS price)`, value: pv.mapped.gap });
    }
    if (pv.discount && pv.discount.amount >= 0.005) {
        rows.push({ key: 'discount', label: `Discount — billed below menu price (${pvPlates(pv.discount.plates)})`, value: -pv.discount.amount });
    }
    if (pv.aboveMenu && pv.aboveMenu.amount >= 0.005) {
        rows.push({ key: 'above', label: `Billed above menu price (${pvPlates(pv.aboveMenu.plates)})`, value: pv.aboveMenu.amount });
    }
    return (
        <div className="gp-price-variance" data-testid="price-variance">
            <p className="gp-price-variance-title">
                Why avg sold is ₹{avg.toFixed(2)}, not the menu price ₹{Number(pv.menuPrice).toFixed(2)}
            </p>
            <div className="gp-price-variance-row">
                <span>Menu price ₹{Number(pv.menuPrice).toFixed(2)} × {pvPlates(pv.plates)}</span>
                <span>{pvMoney(pv.menuValue)}</span>
            </div>
            {rows.map((r) => (
                <div key={r.key} className={`gp-price-variance-row is-${r.key}`}>
                    <span>{r.label}</span>
                    <span>{pvSigned(r.value)}</span>
                </div>
            ))}
            <div className="gp-price-variance-row is-total">
                <span>Sales</span>
                <span>{pvMoney(pv.revenue)}</span>
            </div>
        </div>
    );
};

// ───────────────────────────────────────────────────────────────────────────────
// Ingredient cost breakdown — the "View more" screen behind an item card.
//
// The card can only ever show ONE cost per plate. An ingredient's price moves with
// every purchase batch, so that single number hides the question the reader actually
// has: is this a stable cost, or the tail end of a month that ran between ₹0.140/g
// and ₹0.162/g? This screen answers it, ingredient by ingredient, batch by batch.
// ───────────────────────────────────────────────────────────────────────────────

const ICB_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const icbFmtDate = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

/** Parse a YYYY-MM-DD string as a LOCAL date — `new Date('2026-08-01')` is UTC and
 *  lands on 31 Jul for anyone west of Greenwich. */
const icbParseDate = (value) => {
    const parts = String(value || '').slice(0, 10).split('-');
    if (parts.length !== 3) return new Date();
    return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
};

/** Week (Mon–Sun) or calendar month containing `anchor`. */
const icbRangeFor = (anchor, mode) => {
    const d = icbParseDate(anchor);
    if (mode === 'month') {
        return {
            start: icbFmtDate(new Date(d.getFullYear(), d.getMonth(), 1)),
            end: icbFmtDate(new Date(d.getFullYear(), d.getMonth() + 1, 0))
        };
    }
    const dow = d.getDay();
    const start = new Date(d);
    start.setDate(d.getDate() - (dow === 0 ? 6 : dow - 1));
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    return { start: icbFmtDate(start), end: icbFmtDate(end) };
};

const icbRangeLabel = (start, end) => {
    if (!start || !end) return '';
    const s = icbParseDate(start);
    const e = icbParseDate(end);
    const sameYear = s.getFullYear() === e.getFullYear();
    const left = `${String(s.getDate()).padStart(2, '0')} ${ICB_MONTHS[s.getMonth()]}${sameYear ? '' : ` ${s.getFullYear()}`}`;
    const right = `${String(e.getDate()).padStart(2, '0')} ${ICB_MONTHS[e.getMonth()]} ${e.getFullYear()}`;
    return `${left} – ${right}`;
};

const icbShortDate = (value) => {
    const d = icbParseDate(value);
    return `${String(d.getDate()).padStart(2, '0')} ${ICB_MONTHS[d.getMonth()]}`;
};

const icbMoney = (n, digits = 2) => {
    const value = Number(n);
    if (!Number.isFinite(value)) return '—';
    return `₹${value.toLocaleString('en-IN', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
};

/** Sub-rupee rates need three decimals to be distinguishable at all (₹0.140 vs ₹0.142);
 *  a ₹120/kg rate does not, and printing ₹120.000 just adds noise. */
const icbRate = (n) => {
    const value = Number(n);
    if (!Number.isFinite(value) || value === 0) return '—';
    return icbMoney(value, Math.abs(value) < 1 ? 3 : 2);
};

const icbQty = (n) => {
    const value = Number(n);
    if (!Number.isFinite(value)) return '—';
    const rounded = Math.abs(value) >= 100 ? Math.round(value) : Math.round(value * 1000) / 1000;
    return rounded.toLocaleString('en-IN', { maximumFractionDigits: 3 });
};

/** A paisa. Anything the screen recomputes must agree with the backend to within this,
 *  or the disagreement is shown rather than smoothed over. */
const ICB_PAISA = 0.01;

/**
 * The weighted average rate, recomputed from EXACTLY the rows rendered above it.
 *
 * Never from a pre-computed value passed alongside them: an equation that is printed
 * rather than derived can drift from the rows it claims to summarise, and then the proof
 * on screen is no longer proof of anything. The carried-forward opening row is context,
 * not a purchase, so it is excluded whenever the period contains a real one — the backend
 * marks which rows those are and this mirrors that marking, it does not re-decide it.
 */
/**
 * Identity of ONE displayed row.
 *
 * Not the item id: the same ingredient can legitimately appear twice on one plate — once
 * directly and once as a component of a preparation, or once in the recipe and once in a
 * complimentary side. Keying on the item alone made both rows expand together and made
 * React reuse one row's changelog for the other.
 */
const icbRowId = (ing) => [
    ing.tier || 'recipe',
    ing.origin || '',
    ing.via || '',
    ing.itemId != null ? `id:${ing.itemId}` : `name:${ing.name}`,
].join('|');

const icbRecomputeAvgRate = (rows) => {
    const usable = (rows || []).filter((r) => !r.excludedFromAverage);
    if (usable.length === 0) return null;
    const qty = usable.reduce((s, r) => s + (r.qty > 0 ? r.qty : 0), 0);
    if (qty > 0) {
        return usable.reduce((s, r) => s + r.rate * (r.qty > 0 ? r.qty : 0), 0) / qty;
    }
    return usable.reduce((s, r) => s + r.rate, 0) / usable.length;
};

const ItemCostBreakdownRow = ({ ing, isOpen, onToggle }) => {
    const changes = ing.changes || [];
    const rates = changes.map((c) => c.rate).filter((r) => r > 0);
    const lo = rates.length ? Math.min(...rates) : 0;
    const hi = rates.length ? Math.max(...rates) : 0;
    const inPeriodRates = changes.filter((c) => !c.excludedFromAverage).map((c) => c.rate);
    const trend = inPeriodRates.length > 1
        ? inPeriodRates[inPeriodRates.length - 1] - inPeriodRates[0]
        : 0;

    const avgAvailable = ing.avgAvailable !== false;
    const recomputedRate = icbRecomputeAvgRate(changes);
    const rateMismatch = Boolean(
        avgAvailable
        && ing.avgRate != null
        && recomputedRate != null
        && Math.abs(recomputedRate - ing.avgRate) > ICB_PAISA
    );

    // Reported, not silently corrected: the published figure stays on screen — it is the
    // one the totals were built from — and the marker says the proof under it disagrees.
    useEffect(() => {
        if (rateMismatch) {
            console.warn(
                `[ICB] Weighted-average mismatch for "${ing.name}": rows recompute to `
                + `${recomputedRate} but the endpoint published ${ing.avgRate}.`
            );
        }
    }, [rateMismatch, recomputedRate, ing.avgRate, ing.name]);

    const qtyLabel = ing.qtyPerPlate != null
        ? `${icbQty(ing.qtyPerPlate)} ${ing.unit}`
        : `${icbQty(ing.recipeQty)} ${ing.recipeUnit}`;

    return (
        <li className="icb-row-wrap">
            <button
                type="button"
                className={`icb-row${isOpen ? ' is-open' : ''}`}
                onClick={onToggle}
            >
                <span className="icb-col-name">
                    <span className="icb-row-title">
                        {trend > 0 ? (
                            <TrendingUp size={15} className="icb-trend-up" />
                        ) : trend < 0 ? (
                            <TrendingDown size={15} className="icb-trend-down" />
                        ) : (
                            <Minus size={15} className="icb-trend-flat" />
                        )}
                        <span className="icb-row-name">{ing.name}</span>
                        {ing.isPreparation && <span className="icb-tag">PREP</span>}
                        {ing.tier === 'complimentary' && <span className="icb-tag">FREE</span>}
                        {ing.estimated && <span className="icb-tag">EST</span>}
                        {ing.unpriced && <span className="icb-tag icb-tag-bad">NO PRICE</span>}
                    </span>
                    <span className="icb-row-sub">
                        {qtyLabel}
                        {ing.isPreparation && ing.preparationName ? ` · via ${ing.preparationName}` : ''}
                        {' · '}
                        {ing.carriedForward
                            ? 'no purchase — carried forward'
                            : `${ing.changeCount} change${ing.changeCount === 1 ? '' : 's'}`}
                        {rates.length > 1 ? ` · ${icbRate(lo)}–${icbRate(hi)}` : ''}
                    </span>
                    {/* A missing average, labelled honestly, beats a wrong one. The label is
                        the backend's own words so the screen cannot describe the suppression
                        differently from the reason that caused it. */}
                    {!avgAvailable && (
                        <span className="icb-avg-na">{ing.avgUnavailableLabel}</span>
                    )}
                    {rateMismatch && (
                        <span className="icb-mismatch">
                            mismatch — the rows below do not recompute to this average; please report this
                        </span>
                    )}
                </span>
                <span className="icb-col-rate">
                    {avgAvailable && ing.avgRate != null ? (
                        <>
                            {icbRate(ing.avgRate)}
                            <span className="icb-col-rate-unit">per {ing.unit || ing.recipeUnit}</span>
                        </>
                    ) : (
                        <>
                            —
                            <span className="icb-col-rate-unit">no average</span>
                        </>
                    )}
                </span>
                <span className="icb-col-cost">
                    {icbMoney(avgAvailable ? ing.avgCost : ing.currentCost)}
                    {!avgAvailable && <span className="icb-col-cost-note">current</span>}
                </span>
            </button>

            {isOpen && (
                <div className="icb-changelog-wrap">
                    <div className="icb-changelog">
                        <div className="icb-changelog-head">
                            <p className="icb-changelog-title">{ing.name} — price changes this period</p>
                            <p className="icb-changelog-qty">
                                {icbQty(ing.totalQtyPurchased)} {ing.unit} purchased
                            </p>
                        </div>
                        {changes.length === 0 ? (
                            <p className="icb-changelog-empty">
                                No purchase batch on record for this ingredient.
                            </p>
                        ) : (
                            <table className="icb-table">
                                <thead>
                                    <tr>
                                        <th className="icb-th-left">Date</th>
                                        <th>Rate / {ing.unit || ing.recipeUnit}</th>
                                        <th>Qty in</th>
                                        <th>Value</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {changes.map((c) => (
                                        <tr
                                            key={c.batchId}
                                            className={c.excludedFromAverage ? 'icb-tr-excluded' : undefined}
                                        >
                                            <td className="icb-td-date">
                                                {icbShortDate(c.date)}
                                                <span className="icb-batch">
                                                    {c.batch}
                                                    {c.carriedForward ? ' · carried forward' : ''}
                                                    {/* Shown so "compared to what?" has an answer, and marked
                                                        so nobody adds it into the average by eye. */}
                                                    {c.excludedFromAverage ? ' · opening rate, not in the average' : ''}
                                                </span>
                                            </td>
                                            <td className="icb-td-num icb-td-strong">{icbRate(c.rate)}</td>
                                            <td className="icb-td-num icb-td-muted">
                                                {icbQty(c.qty)} {ing.unit}
                                            </td>
                                            <td className="icb-td-num">{icbMoney(c.rate * c.qty)}</td>
                                        </tr>
                                    ))}
                                    {avgAvailable && ing.avgRate != null ? (
                                        <tr className="icb-tr-total">
                                            <td colSpan={2}>
                                                Weighted average rate
                                                <span className="icb-eq-basis">
                                                    {changes.filter((c) => !c.excludedFromAverage).length} row(s) in the sum
                                                </span>
                                            </td>
                                            <td className="icb-td-num icb-td-muted">× {qtyLabel}</td>
                                            <td className="icb-td-num icb-td-result">
                                                {icbRate(rateMismatch ? ing.avgRate : (recomputedRate ?? ing.avgRate))}
                                                {' → '}
                                                {icbMoney(ing.avgCost)}
                                            </td>
                                        </tr>
                                    ) : (
                                        <tr className="icb-tr-total">
                                            <td colSpan={3}>{ing.avgUnavailableLabel || 'Average unavailable for this line'}</td>
                                            <td className="icb-td-num icb-td-result">
                                                {icbMoney(ing.currentCost)}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            )}
        </li>
    );
};

const ItemCostBreakdownScreen = ({ open, onClose, menuItemId, fallbackName, fallbackImage, anchorDate, language }) => {
    const [mode, setMode] = useState('month');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [expanded, setExpanded] = useState(null);

    const range = icbRangeFor(anchorDate, mode);
    const rangeStart = range.start;
    const rangeEnd = range.end;

    // Reset to the default view whenever a DIFFERENT item is opened, so the previous
    // item's expanded ingredient and period toggle do not carry over.
    useEffect(() => {
        if (!open) return;
        setMode('month');
        setExpanded(null);
    }, [open, menuItemId]);

    useEffect(() => {
        if (!open || !menuItemId) return undefined;

        let cancelled = false;
        const load = async () => {
            setLoading(true);
            setError(null);
            try {
                const businessId = Number(localStorage.getItem('businessId'));
                const url = `${API_BASE_URL}/stockrepo/item-cost-breakdown`
                    + `?businessId=${businessId}`
                    + `&menuItemId=${menuItemId}`
                    + `&weekStart=${rangeStart}&weekEnd=${rangeEnd}`
                    + `&language=${language || 'en'}`;
                const res = await fetch(url);
                if (!res.ok) throw new Error(`Request failed (${res.status})`);
                const json = await res.json();
                if (cancelled) return;
                setData(json);
                // Open the costliest ingredient by default — the one the reader came for.
                const top = [...(json.ingredients || [])].sort((a, b) => b.currentCost - a.currentCost)[0];
                setExpanded(top ? icbRowId(top) : null);
            } catch (e) {
                if (!cancelled) {
                    setError('Could not load the cost breakdown for this item.');
                    setData(null);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => { cancelled = true; };
    }, [open, menuItemId, rangeStart, rangeEnd, language]);

    // Escape closes, and the page behind must not scroll while this is up.
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = previousOverflow;
        };
    }, [open, onClose]);

    if (!open) return null;

    const item = data?.item || {};
    const name = item.name || fallbackName || 'Item';
    const image = item.image || fallbackImage || '';
    const ingredients = data?.ingredients || [];
    const recipeLines = ingredients.filter((i) => i.tier !== 'complimentary');
    const complimentaryLines = ingredients.filter((i) => i.tier === 'complimentary');

    // THE headline: the engine's published per-plate cost, which is the number the card
    // this screen was opened from prints. Never re-summed here — that is precisely the
    // defect this screen used to carry.
    const headlineCogs = Number(data?.cogsPerPlate ?? data?.currentCogsPerPlate ?? 0);
    const avgCogs = Number(data?.avgCogsPerPlate ?? 0);
    const grossProfitPerPlate = Number(data?.grossProfitPerPlate ?? 0);
    const netProfitPerPlate = data?.netGrossProfitPerPlate;
    const netMargin = data?.netMarginPct;
    const grossMargin = data?.grossMarginPct;
    const bridge = data?.marginBridge || {};
    const equation = data?.cogsEquation || null;
    const reconciliation = data?.reconciliation || null;
    const changeCount = Number(data?.changeCount ?? 0);
    const weightingLabel = data?.weighting?.label || 'purchase-quantity weighted';
    // Every ingredient priced from a carried-forward batch means the "average" for this
    // period is just the standing price. Saying "weighted average" then would be a lie.
    const hasRealAverage = changeCount > 0;

    // The equation is recomputed from the rows actually rendered, not read off the
    // payload, so what the reader can add up and what the screen claims cannot diverge.
    const shownRecipeSubtotal = recipeLines.reduce((s, i) => s + Number(i.currentCost || 0), 0);
    const shownComplimentarySubtotal = complimentaryLines.reduce((s, i) => s + Number(i.currentCost || 0), 0);
    const shownReconciliation = reconciliation ? Number(reconciliation.amount || 0) : 0;
    const shownTotal = shownRecipeSubtotal + shownComplimentarySubtotal + shownReconciliation;
    const equationDrifts = Math.abs(shownTotal - headlineCogs) > ICB_PAISA;

    const renderLines = (lines) => lines.map((ing) => {
        const rowId = icbRowId(ing);
        return (
            <ItemCostBreakdownRow
                key={rowId}
                ing={ing}
                isOpen={expanded === rowId}
                onToggle={() => setExpanded(expanded === rowId ? null : rowId)}
            />
        );
    });

    const body = (
        <div className="icb-screen" role="dialog" aria-modal="true" aria-label={`Cost breakdown for ${name}`}>
            {/* ── Top bar ── */}
            <div className="icb-topbar">
                <div className="icb-topbar-inner">
                    <button className="icb-back" onClick={onClose} aria-label="Back to report">
                        <ArrowLeft size={16} />
                    </button>
                    <div className="icb-topbar-title">
                        <p className="icb-eyebrow">Cost breakdown</p>
                        <h1 className="icb-item-name">{name}</h1>
                    </div>
                    <div className="icb-toggle" role="tablist" aria-label="Period">
                        {['week', 'month'].map((p) => (
                            <button
                                key={p}
                                role="tab"
                                aria-selected={mode === p}
                                className={`icb-toggle-btn${mode === p ? ' is-active' : ''}`}
                                onClick={() => setMode(p)}
                            >
                                {p === 'week' ? 'Week' : 'Month'}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <div className="icb-body">
                {loading && (
                    <div className="icb-state">
                        <div className="icb-spinner" />
                        <p>Loading price history…</p>
                    </div>
                )}

                {!loading && error && (
                    <div className="icb-state icb-state-error">
                        <p>{error}</p>
                    </div>
                )}

                {/* The screen is gated per business while it rolls out. Off means the report
                    behaves exactly as it did before the feature existed. */}
                {!loading && !error && data && data.featureEnabled === false && (
                    <div className="icb-state">
                        <p>The detailed cost breakdown is not enabled for this business yet.</p>
                    </div>
                )}

                {!loading && !error && data?.featureEnabled !== false && data?.cogsUnavailable && (
                    <div className="icb-state icb-state-error">
                        <p>
                            This item&apos;s cost cannot be stated for {icbRangeLabel(rangeStart, rangeEnd)} —
                            at least one ingredient has no price on file.
                        </p>
                        {Array.isArray(data.uncoveredIngredients) && data.uncoveredIngredients.length > 0 && (
                            <ul className="icb-uncovered">
                                {data.uncoveredIngredients.map((u, i) => (
                                    <li key={`${u.name}-${i}`}>
                                        <strong>{u.name}</strong>
                                        {u.via ? ` (via ${u.via})` : ''}
                                        {' — needs '}{icbQty(u.needed)}
                                        {', net available '}{icbQty(u.netAvailable)}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}

                {!loading && !error && data && data.featureEnabled !== false && !data.cogsUnavailable && (
                    <>
                        {/* ── Hero ── */}
                        <section className="icb-card icb-hero">
                            <div className="icb-hero-top">
                                {image && !isPlaceholderImage(image) ? (
                                    <img
                                        src={image}
                                        alt={name}
                                        className="icb-hero-img"
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                ) : (
                                    <div className="icb-hero-img icb-hero-img-placeholder">
                                        <Utensils size={26} />
                                    </div>
                                )}
                                <div className="icb-hero-meta">
                                    <h2 className="icb-hero-name">{name}</h2>
                                    <p className="icb-hero-sub">
                                        Menu price {icbMoney(item.price)} · {Number(item.sold || 0)} plates sold
                                        · {icbRangeLabel(rangeStart, rangeEnd)}
                                    </p>
                                    <div className="icb-chips">
                                        <span className="icb-chip icb-chip-neutral">
                                            {ingredients.length} ingredient{ingredients.length === 1 ? '' : 's'}
                                        </span>
                                        <span className="icb-chip icb-chip-warn">
                                            {changeCount} price change{changeCount === 1 ? '' : 's'} this {mode}
                                        </span>
                                        <span className="icb-chip icb-chip-good">
                                            {hasRealAverage ? weightingLabel : 'no purchase this period'}
                                        </span>
                                        {data.isEstimated && (
                                            <span className="icb-chip icb-chip-warn">estimated</span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Net leads, because net is what the card shows and what the reader
                                came here to verify. Gross sits beside it, labelled, with the
                                bridge between them spelled out below — the two used to be the
                                same word on two screens with two different numbers under it. */}
                            <div className="icb-stats">
                                <div className="icb-stat">
                                    <p className="icb-stat-label">COGS / plate (report)</p>
                                    <p className="icb-stat-value icb-danger">{icbMoney(headlineCogs)}</p>
                                </div>
                                <div className="icb-stat icb-stat-lead">
                                    <p className="icb-stat-label">Net margin (card figure)</p>
                                    <p className={`icb-stat-value ${netMargin != null && netMargin >= 0 ? 'icb-success' : 'icb-danger'}`}>
                                        {netMargin != null ? `${netMargin.toFixed(1)}%` : '—'}
                                    </p>
                                </div>
                                <div className="icb-stat icb-stat-row2">
                                    <p className="icb-stat-label">Gross margin (before wastage)</p>
                                    <p className={`icb-stat-value ${grossMargin != null && grossMargin >= 0 ? 'icb-success' : 'icb-danger'}`}>
                                        {grossMargin != null ? `${grossMargin.toFixed(1)}%` : '—'}
                                    </p>
                                </div>
                                <div className="icb-stat icb-stat-row2">
                                    <p className="icb-stat-label">Gross profit / plate</p>
                                    <p className={`icb-stat-value ${grossProfitPerPlate >= 0 ? 'icb-success' : 'icb-danger'}`}>
                                        {icbMoney(grossProfitPerPlate)}
                                    </p>
                                </div>
                                <div className="icb-stat icb-stat-row2">
                                    <p className="icb-stat-label">Period avg COGS</p>
                                    <p className="icb-stat-value">{icbMoney(avgCogs)}</p>
                                </div>
                                <div className="icb-stat icb-stat-row2">
                                    <p className="icb-stat-label">Net profit / plate</p>
                                    <p className={`icb-stat-value ${Number(netProfitPerPlate || 0) >= 0 ? 'icb-success' : 'icb-danger'}`}>
                                        {netProfitPerPlate != null ? icbMoney(netProfitPerPlate) : '—'}
                                    </p>
                                </div>
                            </div>

                            {/* The bridge, stated arithmetically so the two margins above are
                                visibly the same story rather than two competing ones. */}
                            <div className="icb-bridge">
                                {data.marginBasis === 'period' ? (
                                    <p>
                                        Gross {icbMoney(bridge.soldGrossProfit)} on {icbMoney(bridge.revenue)} of
                                        sales ({grossMargin != null ? `${grossMargin.toFixed(1)}%` : '—'})
                                        {' − wastage this period: '}
                                        {Number(bridge.wastagePlates || 0)} plate
                                        {Number(bridge.wastagePlates || 0) === 1 ? '' : 's'},{' '}
                                        {icbMoney(bridge.wastageCost)}
                                        {' → net '}
                                        {icbMoney(bridge.netGrossProfit)}
                                        {' ('}{netMargin != null ? `${netMargin.toFixed(1)}%` : '—'}{')'}
                                    </p>
                                ) : (
                                    <p>
                                        Nothing sold in this period, so there is no net margin to state —
                                        the gross figures above are per plate at the menu price.
                                    </p>
                                )}
                                {item.wastageCostUnavailable && (
                                    <p className="icb-bridge-warn">
                                        {Number(item.uncostedWastagePlates || 0)} wasted plate
                                        {Number(item.uncostedWastagePlates || 0) === 1 ? '' : 's'} carry no cost
                                        impact, so the wastage deducted above is not the whole loss.
                                    </p>
                                )}
                                {data.marginImplausible && (
                                    <p className="icb-bridge-warn">
                                        A margin outside −100%…100% is arithmetically impossible for a real
                                        dish — the cost or the revenue behind it needs investigating.
                                    </p>
                                )}
                            </div>
                        </section>

                        {/* ── Ingredient list ── */}
                        <section className="icb-card">
                            <div className="icb-section-head">
                                <h3>Ingredient average cost</h3>
                                <p>Tap a row to see every change · {weightingLabel}</p>
                            </div>

                            <div className="icb-col-head">
                                <span className="icb-col-name">Ingredient</span>
                                <span className="icb-col-rate">Avg rate</span>
                                <span className="icb-col-cost">Avg cost</span>
                            </div>

                            <ul className="icb-list">{renderLines(recipeLines)}</ul>

                            {/* The free sides are part of what the plate costs, so they are part of
                                what explains the plate's cost. Leaving them out is what made this
                                screen disagree with the card on every dish that has one. */}
                            {complimentaryLines.length > 0 && (
                                <>
                                    <div className="icb-tier-head">
                                        <span>Complimentary items</span>
                                        <span className="icb-tier-head-sub">
                                            included in the plate&apos;s COGS
                                        </span>
                                    </div>
                                    <ul className="icb-list">{renderLines(complimentaryLines)}</ul>
                                </>
                            )}

                            {/* ── The arithmetic, closing visibly ── */}
                            <div className="icb-equation">
                                <div className="icb-eq-row">
                                    <span>Recipe subtotal</span>
                                    <span className="icb-eq-value">{icbMoney(shownRecipeSubtotal)}</span>
                                </div>
                                {complimentaryLines.length > 0 && (
                                    <div className="icb-eq-row">
                                        <span>Complimentary subtotal</span>
                                        <span className="icb-eq-value">{icbMoney(shownComplimentarySubtotal)}</span>
                                    </div>
                                )}
                                {reconciliation && (
                                    <div className="icb-eq-row icb-eq-recon">
                                        <span>{reconciliation.label}</span>
                                        <span className="icb-eq-value">
                                            {shownReconciliation >= 0 ? '+' : '−'}
                                            {icbMoney(Math.abs(shownReconciliation))}
                                        </span>
                                    </div>
                                )}
                                <div className="icb-eq-row icb-eq-total">
                                    <span>Total COGS / plate <em>as shown on the report card</em></span>
                                    <span className="icb-eq-value">{icbMoney(headlineCogs)}</span>
                                </div>
                                {equationDrifts && (
                                    <div className="icb-eq-row icb-eq-mismatch">
                                        <span>
                                            mismatch — the rows above do not add up to this total; please report this
                                        </span>
                                        <span className="icb-eq-value">
                                            {icbMoney(headlineCogs - shownTotal)}
                                        </span>
                                    </div>
                                )}
                                {equation && !equation.reconciles && !reconciliation && (
                                    <div className="icb-eq-row icb-eq-mismatch">
                                        <span>unexplained difference of {icbMoney(equation.drift)}</span>
                                        <span className="icb-eq-value">—</span>
                                    </div>
                                )}
                            </div>

                            <div className="icb-total-row">
                                <div>
                                    <p className="icb-total-label">Total average COGS</p>
                                    <p className="icb-total-sub">
                                        per plate · {icbRangeLabel(rangeStart, rangeEnd)} · {weightingLabel}
                                    </p>
                                    {data.avgPartial && (
                                        <p className="icb-total-warn">
                                            avg partial — {data.avgPartialLineCount} line
                                            {data.avgPartialLineCount === 1 ? '' : 's'} at current cost
                                        </p>
                                    )}
                                </div>
                                <p className="icb-total-value">{icbMoney(avgCogs)}</p>
                            </div>
                        </section>

                        {Number(item.wastagePlates) > 0 && (
                            <div className="icb-wastage-note">
                                {Number(item.wastagePlates)} plate{Number(item.wastagePlates) === 1 ? '' : 's'} wasted
                                this period, costing {icbMoney(item.wastageCost)}. That is deducted from the
                                item&apos;s net margin above and on the report card, not from the per-plate
                                COGS.
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );

    // Portalled to <body>: the report modal this opens from is `overflow: hidden`, and a
    // full-screen panel rendered inside it would be clipped to the modal's box.
    return ReactDOM.createPortal(body, document.body);
};

const SalesItemRow = ({ item, isLast, modalSearch, anchorDate, language }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [showBreakdown, setShowBreakdown] = useState(false);
    const bd = item.marginBreakdown;

    return (
        <div className={`srx-item-row${isOpen ? ' is-open' : ''}`} style={isLast ? { marginBottom: '8px' } : {}}>
            <div className="item-wise-sales-item-card" onClick={() => bd && setIsOpen(!isOpen)} style={bd ? { cursor: 'pointer' } : {}}>
                {item.image && !isPlaceholderImage(item.image) ? (
                    <>
                        <img
                            src={item.image}
                            alt={item.name}
                            className="item-wise-sales-image"
                            onError={(e) => {
                                e.target.style.display = 'none';
                                const placeholder = e.target.parentNode.querySelector('.item-wise-sales-image-placeholder');
                                if (placeholder) placeholder.style.display = 'flex';
                            }}
                        />
                        <div className="item-wise-sales-image-placeholder" style={{ display: 'none' }}>
                            <Utensils size={18} className="placeholder-food-icon" />
                        </div>
                    </>
                ) : (
                    <div className="item-wise-sales-image-placeholder">
                        <Utensils size={18} className="placeholder-food-icon" />
                    </div>
                )}

                <div className="item-wise-sales-details">
                    <h4 className="item-wise-sales-name">
                        {highlightMatch(item.name, modalSearch)}
                    </h4>
                    <p className="item-wise-sales-info">
                        <span className="item-wise-sales-price">{item.price}</span>
                        <span className="item-wise-sales-dot">•</span>
                        <span
                            className="item-wise-sales-sold"
                            role="button"
                            tabIndex={0}
                            title="View ingredient and batch cost breakdown"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (bd) {
                                    setIsOpen(!isOpen);
                                }
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (bd) {
                                        setIsOpen(!isOpen);
                                    }
                                }
                            }}
                            style={{
                                cursor: bd ? 'pointer' : 'default',
                                textDecoration: 'none'
                            }}
                        >
                            {item.sold}
                        </span>
                    </p>
                    {item.wastage ? (
                        <p className="item-wise-sales-wastage">
                            {item.wastage}
                        </p>
                    ) : null}
                </div>

                <div className="item-wise-sales-profit-section">
                    <div className="iws-metric-row iws-revenue-row">
                        <span className="item-wise-sales-profit">
                            {item.grossProfit}
                        </span>
                    </div>
                    <div className="iws-metric-row iws-margin-row">
                        {item.margin != null ? (
                            <div className="margin-chevron-row">
                                <span className={`item-wise-sales-change ${item.margin < 0 ? 'negative' : 'positive'}`}>
                                    {item.margin.toFixed(1)}%
                                </span>
                                {bd && <IoChevronDown className={`srx-chevron${isOpen ? ' is-open' : ''}`} />}
                            </div>
                        ) : (
                            <div className="margin-chevron-row">
                                <span className="item-wise-sales-change" style={{ color: '#666' }}>-</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {bd && (
                <div className={`srx-panel${isOpen ? ' is-open' : ''}`}>
                    <div className="srx-panel-body">
                        <div className="gp-breakdown-dropdown">
                            {(() => {
                                const grossPct = bd.grossMargin != null ? bd.grossMargin : item.margin;
                                const hasWastage = Number(bd.wastageCost) > 0;
                                // An unpriceable plate sends null for every money field it cannot
                                // state. Rendering those with .toFixed() threw — the other half of why
                                // these rows never opened — so all money goes through one formatter
                                // that prints a dash rather than inventing a zero.
                                const money = (v) => (v == null ? '—' : `₹${Number(v).toFixed(2)}`);
                                const negatives = Array.isArray(bd.negativeStockIngredients) ? bd.negativeStockIngredients : [];
                                return (
                                    <div className="gp-formula-box">
                                        <div className="gp-formula-line">
                                            <span className="gp-formula-label">Avg sold</span>
                                            <span className="gp-formula-item gp-formula-price">₹{Number(bd.realisedPrice ?? bd.sellingPrice).toFixed(2)}</span>
                                            <span className="gp-formula-op">−</span>
                                            <span className="gp-formula-label">Cost</span>
                                            <span className="gp-formula-item gp-formula-cost">{money(bd.totalCOGS)}</span>
                                            <span className="gp-formula-op">=</span>
                                            <span className={`gp-formula-item ${bd.grossProfit == null ? '' : (bd.grossProfit >= 0 ? 'gp-formula-profit' : 'gp-formula-loss')}`}>{money(bd.grossProfit)}</span>
                                            {grossPct != null && <span className={`gp-formula-pct ${grossPct >= 30 ? 'gp-pct-good' : grossPct >= 0 ? 'gp-pct-warn' : 'gp-pct-bad'}`}>({grossPct.toFixed(1)}%)</span>}
                                        </div>
                                        <PriceVarianceNote pv={bd.priceVariance} />
                                        {hasWastage && (
                                            <div className="gp-formula-line gp-formula-wastage-line">
                                                <span className="gp-formula-label">Gross</span>
                                                <span className="gp-formula-item gp-formula-price">{money(bd.totalSoldGrossProfit)}</span>
                                                <span className="gp-formula-op">−</span>
                                                <span className="gp-formula-label">Wastage</span>
                                                <span className="gp-formula-item gp-formula-cost">₹{Number(bd.wastageCost).toFixed(2)}</span>
                                                <span className="gp-formula-op">=</span>
                                                <span className={`gp-formula-item ${bd.netGrossProfit == null ? '' : (bd.netGrossProfit >= 0 ? 'gp-formula-profit' : 'gp-formula-loss')}`}>{money(bd.netGrossProfit)}</span>
                                                {item.margin != null && <span className={`gp-formula-pct ${item.margin >= 30 ? 'gp-pct-good' : item.margin >= 0 ? 'gp-pct-warn' : 'gp-pct-bad'}`}>({item.margin.toFixed(1)}%)</span>}
                                            </div>
                                        )}
                                        {/* Name the culprit. The dash above says the plate cannot be
                                            priced; this says WHICH ingredient did it and how far into
                                            the red it is — both straight from the batch ledger. */}
                                        {bd.cogsUnavailable && (
                                            <div className="srx-negative-note">
                                                {negatives.length > 0 ? (
                                                    <>
                                                        <strong>Cost unavailable</strong> — negative stock:{' '}
                                                        {negatives.map((n, k) => (
                                                            <span key={k}>
                                                                {k > 0 ? ', ' : ''}
                                                                <strong>{n.name}</strong>
                                                                {n.via ? ` (via ${n.via})` : ''}
                                                                {n.netAvailable != null ? ` — in stock ${n.netAvailable}` : ''}
                                                                {n.needed != null ? `, needs ${n.needed}` : ''}
                                                            </span>
                                                        ))}
                                                    </>
                                                ) : (
                                                    <>
                                                        <strong>Cost unavailable</strong>
                                                        {bd.cogsUnavailableReason === 'no_recipe_or_ingredients'
                                                            ? ' — this item has no costable recipe.'
                                                            : ' — an ingredient has no price on file.'}
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })()}

                            {/* `null <= 0` is TRUE in JavaScript, so without this null test an
                                unpriceable plate would be accused of costing zero — the opposite
                                of what its dash means. */}
                            {bd.totalCOGS != null && bd.totalCOGS <= 0 && (
                                <div className="gp-zero-cost-note" style={{
                                    margin: '12px 0 4px',
                                    padding: '10px 12px',
                                    borderRadius: '10px',
                                    background: '#fff7ed',
                                    border: '1px solid #fdba74',
                                    color: '#9a3412',
                                    fontSize: '13px',
                                    lineHeight: 1.45
                                }}>
                                    COGS is zero for this item. Check whether ingredient costs are missing, unit conversion could not be resolved, or no positive batch price was available.
                                </div>
                            )}

                            {bd.ingredients && bd.ingredients.length > 0 && (
                                <div className="gp-breakdown-section srx-ingredients-section">
                                    <div className="ingredients-list-container" style={{ borderTop: 'none' }}>
                                        <div className="ingredients-list-header">
                                            <span className="ing-col-name">
                                                Ingredient
                                                <span className="ing-count-tag">{bd.ingredientCount ?? bd.ingredients.length}</span>
                                            </span>
                                            <span className="ing-col-qty">Qty</span>
                                            <span className="ing-col-cost">Cost</span>
                                        </div>
                                        {bd.ingredients.map((ing, i) => (
                                            <div key={i} className={`ingredients-list-row${ing.tier === 'complimentary' ? ' complimentary-row' : ''}`}>
                                                <span className="ing-col-name">
                                                    {ing.name}
                                                    {/* On the unified path the free sides are rows in THIS list rather
                                                        than a separate block below it, so they carry the tag here. */}
                                                    {ing.tier === 'complimentary' && <span className="ing-comp-tag">FREE</span>}
                                                    {ing.isPreparation && <span className="ing-comp-tag">PREP</span>}
                                                    {ing.isPreparation && ing.preparationName ? ` (${ing.preparationName})` : ''}
                                                    {ing.negativeStock && (
                                                        <span
                                                            className="ing-negative-tag"
                                                            title={`Negative stock: ${ing.stockAvailable != null ? ing.stockAvailable : 'below zero'} on hand`
                                                                + `${ing.stockNeeded != null ? `, this plate needs ${ing.stockNeeded}` : ''}`
                                                                + ' — cost cannot be stated for this ingredient'}
                                                        >NEGATIVE STOCK</span>
                                                    )}
                                                    {ing.unpriced && !ing.negativeStock && <span className="ing-unpriced-tag" title="No price on file — this plate's cost is understated">NO PRICE</span>}
                                                    {ing.estimated && <span className="ing-comp-tag" title="Priced from an old purchase price, not from stock on hand">EST</span>}
                                                </span>
                                                {/* THE NEGATIVE LINE, AND ONLY IT.
                                                    `negativeStock` is set per ingredient from the coverage
                                                    ladder's own verdict against the batch ledger, so the dash
                                                    lands on the one ingredient actually in the red while every
                                                    other row on the same plate keeps its real quantity and its
                                                    real cost. Marking the whole item would destroy the part of
                                                    the breakdown that IS measured. */}
                                                <span className="ing-col-qty" title={ing.negativeStock && ing.stockNeeded != null ? `Recipe needs ${ing.stockNeeded} ${ing.unit || ''}` : undefined}>
                                                    {ing.negativeStock ? '—' : formatQuantityParts(ing.qty, ing.unit).label}
                                                </span>
                                                <span className={`ing-col-cost${(ing.unpriced || ing.negativeStock) ? ' is-unpriced' : ''}`}>
                                                    {(ing.negativeStock || ing.unpriced || ing.cost == null)
                                                        ? '—'
                                                        : (ing.cost === 0 && Number(ing.costExact) > 0
                                                            ? '<₹0.01'
                                                            : `₹${ing.cost.toFixed(2)}`)}
                                                </span>
                                            </div>
                                        ))}
                                        {/* Only when these are NOT already rows above. On the unified path
                                            `ingredients` is a complete account of the plate — free sides
                                            included — so rendering this block as well would print every
                                            complimentary cost twice and make the subtotal look wrong again. */}
                                        {!bd.complimentaryAreRollupOfIngredients && bd.complimentary && bd.complimentary.map((ing, i) => (
                                            <div key={`c-${i}`} className="ingredients-list-row complimentary-row">
                                                <span className="ing-col-name">
                                                    {ing.name}
                                                    <span className="ing-comp-tag">FREE</span>
                                                    {ing.fromPreparation && <span className="ing-comp-tag">PREP</span>}
                                                    {ing.fromPreparation && ing.preparationName ? ` (${ing.preparationName})` : ''}
                                                    {/* A free side can be the short line too — Cappuccino's
                                                        sugar sachet is exactly that case. */}
                                                    {ing.negativeStock && (
                                                        <span
                                                            className="ing-negative-tag"
                                                            title={`Negative stock: ${ing.stockAvailable != null ? ing.stockAvailable : 'below zero'} on hand`
                                                                + `${ing.stockNeeded != null ? `, this plate needs ${ing.stockNeeded}` : ''}`
                                                                + ' — cost cannot be stated for this ingredient'}
                                                        >NEGATIVE STOCK</span>
                                                    )}
                                                </span>
                                                <span className="ing-col-qty">
                                                    {ing.negativeStock ? '—' : formatQuantityParts(ing.qty, ing.unit).label}
                                                </span>
                                                <span className={`ing-col-cost${ing.negativeStock ? ' is-unpriced' : ''}`}>
                                                    {ing.cost == null ? '—' : `₹${Number(ing.cost).toFixed(2)}`}
                                                </span>
                                            </div>
                                        ))}
                                        {/* The three-tier equation: recipe + complimentary = Total COGS.
                                            Shown only when the rows and the total came from ONE computation,
                                            so the arithmetic printed here is guaranteed to hold. Otherwise
                                            the single subtotal below is shown exactly as before. */}
                                        {bd.cogsEquation ? (
                                            <>
                                                <div className="ingredients-list-subtotal">
                                                    <span className="ing-col-name">Recipe subtotal</span>
                                                    <span className="ing-col-qty"></span>
                                                    <span className="ing-col-cost">₹{Number(bd.cogsEquation.recipeSubtotal).toFixed(2)}</span>
                                                </div>
                                                {bd.cogsEquation.complimentarySubtotal > 0 && (
                                                    <div className="ingredients-list-subtotal">
                                                        <span className="ing-col-name">Complimentary subtotal</span>
                                                        <span className="ing-col-qty"></span>
                                                        <span className="ing-col-cost">₹{Number(bd.cogsEquation.complimentarySubtotal).toFixed(2)}</span>
                                                    </div>
                                                )}
                                            </>
                                        ) : (bd.ingredientsSubtotal != null && (
                                            <div className="ingredients-list-subtotal">
                                                <span className="ing-col-name">Ingredients subtotal</span>
                                                <span className="ing-col-qty"></span>
                                                <span className="ing-col-cost">₹{Number(bd.ingredientsSubtotal).toFixed(2)}</span>
                                            </div>
                                        ))}
                                        <div className="ingredients-list-total">
                                            <span className="ing-col-name">Total COGS <span className="ing-perplate-tag">per plate</span></span>
                                            <span className="ing-col-qty"></span>
                                            <span className="ing-col-cost">{bd.totalCOGS == null ? '—' : `₹${bd.totalCOGS.toFixed(2)}`}</span>
                                        </div>
                                        {bd.unpricedCount > 0 && (
                                            <div className="ing-unpriced-note">
                                                {bd.unpricedCount} ingredient{bd.unpricedCount === 1 ? '' : 's'} have no price on file — this plate's cost is understated.
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* The list above is one snapshot of a cost that moved all period.
                                This opens the batch-by-batch history behind it. */}
                            {item.menuItemId != null && (
                                <div className="srx-viewmore-wrap">
                                    <button
                                        type="button"
                                        className="srx-viewmore-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setShowBreakdown(true);
                                        }}
                                    >
                                        <span>View More — full cost breakdown</span>
                                        <ArrowUpRight size={16} />
                                    </button>
                                    <p className="srx-viewmore-hint">
                                        See every price change behind this average COGS
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <ItemCostBreakdownScreen
                open={showBreakdown}
                onClose={() => setShowBreakdown(false)}
                menuItemId={item.menuItemId}
                fallbackName={item.name}
                fallbackImage={item.image}
                anchorDate={anchorDate}
                language={language}
            />
        </div>
    );
};

export { isPlaceholderImage, ItemCostBreakdownScreen };
export default SalesItemRow;
